import type { SortingState } from '@tanstack/react-table';
import type { ServerPaginationInit } from '../hooks/useServerPagination';

export function encodeCursorStack(keys: (string | null)[]): string {
  return btoa(JSON.stringify(keys));
}

export function decodeCursorStack(raw: string | null): (string | null)[] | null {
  if (!raw) return null;
  try {
    const v = JSON.parse(atob(raw)) as unknown;
    if (!Array.isArray(v)) return null;
    return v as (string | null)[];
  } catch {
    return null;
  }
}

export const CURSORS_PARAM_MAX_LENGTH = 1800;

export function readListTableSearch(sp: URLSearchParams): {
  page: number;
  sortId: string;
  orderDesc: boolean;
  cursors: (string | null)[] | null;
} {
  const page = Math.max(0, parseInt(sp.get('page') || '0', 10) || 0);
  const sortId = sp.get('sort') || '';
  const orderDesc = sp.get('order') === 'desc';
  const cursors = sortId ? null : decodeCursorStack(sp.get('cursors'));
  return { page, sortId, orderDesc, cursors };
}

export function serverPaginationInitFromListUrl(
  page: number,
  sortId: string,
  cursors: (string | null)[] | null
): ServerPaginationInit {
  if (sortId) {
    return { initialPage: Math.max(0, page), initialLastKeys: [null] };
  }
  let stack = cursors && cursors.length > 0 ? [...cursors] : [null];
  if (stack[0] !== null) {
    stack.unshift(null);
  }
  const maxPage = Math.max(0, stack.length - 1);
  return {
    initialPage: Math.min(Math.max(0, page), maxPage),
    initialLastKeys: stack,
  };
}

export function initialSortingFromListUrl(sortId: string, orderDesc: boolean): SortingState {
  if (!sortId) return [];
  return [{ id: sortId, desc: orderDesc }];
}

export function readStatusChipParam(sp: URLSearchParams, key = 'status'): string[] {
  const v = sp.get(key);
  return v ? [v] : [];
}

export function readPacksSearchQuery(sp: URLSearchParams): string {
  return sp.get('q') || '';
}

const AUDIT_ENTITY_TYPES = new Set(['PACK', 'CELL', 'CUSTOMER', 'TEST']);
const AUDIT_ACTIONS = new Set(['CREATE', 'UPDATE', 'DELETE', 'SOFT_DELETE']);

export function readAuditFilterParams(sp: URLSearchParams): {
  entityType: 'PACK' | 'CELL' | 'CUSTOMER' | 'TEST' | '';
  entityId: string;
  action: 'CREATE' | 'UPDATE' | 'DELETE' | 'SOFT_DELETE' | '';
} {
  const rawEt = sp.get('entityType') || '';
  const entityType =
    rawEt === '' || AUDIT_ENTITY_TYPES.has(rawEt)
      ? (rawEt as 'PACK' | 'CELL' | 'CUSTOMER' | 'TEST' | '')
      : '';
  const entityId = sp.get('entityId') || '';
  const rawAction = sp.get('action') || '';
  const action =
    rawAction === '' || AUDIT_ACTIONS.has(rawAction)
      ? (rawAction as 'CREATE' | 'UPDATE' | 'DELETE' | 'SOFT_DELETE' | '')
      : '';
  return { entityType, entityId, action };
}

export function searchParamsEffectivelyEqual(
  a: URLSearchParams,
  b: URLSearchParams
): boolean {
  const keys = new Set<string>([...a.keys(), ...b.keys()]);
  for (const k of keys) {
    if (a.get(k) !== b.get(k)) return false;
  }
  return true;
}

export function applyListTableToSearchParams(
  base: URLSearchParams,
  args: {
    page: number;
    sorting: SortingState;
    lastKeys: (string | null)[];
  }
): URLSearchParams {
  const next = new URLSearchParams(base);
  next.set('page', String(args.page));
  if (args.sorting[0]) {
    next.set('sort', args.sorting[0].id);
    next.set('order', args.sorting[0].desc ? 'desc' : 'asc');
    next.delete('cursors');
  } else {
    next.delete('sort');
    next.delete('order');
    try {
      const enc = encodeCursorStack(args.lastKeys);
      if (enc.length <= CURSORS_PARAM_MAX_LENGTH) {
        next.set('cursors', enc);
      } else {
        next.delete('cursors');
      }
    } catch {
      next.delete('cursors');
    }
  }
  return next;
}
